<template>
  <div class="px-2 w-full md:w-1/2 lg:w-1/4 lg:flex-1 flex-none flex flex-col">
    <div class="border my-4 box relative bg-white flex-grow flex flex-col">
      <!-- header: icon, title, tabs, count -->
      <div class="bg-brand-lightest px-3 flex border-b w-full">
        <div class="flex-1 flex flex-col relative h-22">
          <p class="flex-1 py-4">
            <i class="fa text-brand" :class="icon"></i>
            <span class="font-semibold text-xl">{{ title }}</span>
          </p>
          <ul
            class="uppercase flex text-sm2 absolute inset-x-0 bottom-0"
            v-if="tabs.length > 0"
          >
            <li class="px-2" v-for="tab in tabs" :key="tab.key">
              <a
                href="#"
                class="no-underline text-black block py-2 border-b-4 hover:border-brand"
                :class="tab.key === activeTab ? 'border-brand' : 'border-transparent'"
                @click.prevent="$emit('tab', tab.key)"
                >{{ tab.label }}</a
              >
            </li>
          </ul>
        </div>
        <div class="flex-none py-3" v-if="count !== null">
          <div
            class="rounded-full h-16 w-16 border-2 border-brand flex justify-center items-center text-3xl font-light"
          >
            {{ count }}
          </div>
        </div>
      </div>

      <!-- add action and filter toggle -->
      <div
        class="border-b p-4 flex justify-between items-center"
        v-if="addLabel || filterable"
        v-is-authorized="rowGuard"
      >
        <router-link
          v-if="addLabel && addTo"
          :to="addTo"
          class="text-sm2 text-black no-underline"
          v-is-authorized="addGuard"
        >
          <i class="fa fa-plus text-brand"></i>
          <span class="font-semibold">{{ addLabel }}</span>
        </router-link>
        <button
          v-else-if="addLabel"
          type="button"
          class="text-sm2 text-black no-underline"
          v-is-authorized="addGuard"
          @click.prevent="$emit('add')"
        >
          <i class="fa fa-plus text-brand"></i>
          <span class="font-semibold">{{ addLabel }}</span>
        </button>
        <span v-else></span>
        <button
          v-if="filterable"
          type="button"
          class="text-xs text-grey-dark px-1 rounded"
          :class="{ 'text-brand': showFilter }"
          @click.prevent="showFilter = !showFilter"
        >
          <i class="fa fa-filter"></i>
        </button>
      </div>

      <!-- filter dropdown: a zero-height relative anchor keeps it under the
           add row now that the box is a flex column -->
      <div class="relative" v-if="filterable">
        <div
          class="absolute inset-x-0 top-0 bg-white border-b shadow-lg p-4 z-100"
          v-show="showFilter"
        >
          <slot name="filter"></slot>
          <div class="mt-4 flex">
            <button
              type="button"
              class="flex-1 mr-1 border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
              :class="{ 'opacity-50 cursor-not-allowed': !canExport }"
              :disabled="!canExport || downloading"
              @click.prevent="$emit('download')"
            >
              <i class="fa fa-spinner fa-spin mr-1" v-if="downloading"></i>
              Download
            </button>
            <button
              type="button"
              class="flex-1 ml-1 border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
              :class="{ 'opacity-50 cursor-not-allowed': !canExport }"
              :disabled="!canExport || addingToVault"
              @click.prevent="$emit('add-to-vault')"
            >
              <i class="fa fa-spinner fa-spin mr-1" v-if="addingToVault"></i>
              Add to Vault
            </button>
          </div>
        </div>
      </div>

      <!-- list -->
      <div class="content flex-1 min-h-0 overflow-y-auto">
        <p
          class="text-center m-8 h-64 flex items-center justify-center"
          v-if="loading"
        >
          <i class="fa fa-spinner fa-spin mr-1"></i> Please wait...
        </p>
        <slot v-else-if="!empty"></slot>
        <p class="text-center m-8 text-grey-dark" v-else>{{ emptyText }}</p>
      </div>

      <!-- pager -->
      <div
        class="p-2 bg-grey-lighter border-t flex justify-between items-center text-xs text-grey-darker"
        v-if="pages"
      >
        <button
          type="button"
          class="border border-grey bg-white text-grey-darker text-xs font-medium py-1 px-3 rounded hover:bg-grey-lightest"
          :class="{ 'opacity-50 cursor-not-allowed': page <= 1 }"
          :disabled="page <= 1"
          @click.prevent="$emit('page', page - 1)"
        >
          <i class="fa fa-angle-left"></i> Prev
        </button>
        <span>{{ pagerText }}</span>
        <button
          type="button"
          class="border border-grey bg-white text-grey-darker text-xs font-medium py-1 px-3 rounded hover:bg-grey-lightest"
          :class="{ 'opacity-50 cursor-not-allowed': page >= lastPage }"
          :disabled="page >= lastPage"
          @click.prevent="$emit('page', page + 1)"
        >
          Next <i class="fa fa-angle-right"></i>
        </button>
      </div>
    </div>

    <!-- add and view modals of the panel -->
    <slot name="modals"></slot>

    <!-- blocking spinner while the panel posts something -->
    <sweet-modal
      blocking
      pulse-on-block
      hide-close-button
      overlay-theme="dark"
      ref="busy"
      enable-mobile-fullscreen
    >
      <div class="mt-4">
        <p class="text-6xl text-grey-dark">
          <i class="fa fa-spin fa-circle-notch"></i>
        </p>
        {{ busyText }}
      </div>
    </sweet-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { hasPages, pagerLabel } from "../../../dashboard_pager.mjs";

/**
 * Shared shell of the five vault dashboard panels (Key Document, File & Tag,
 * Discussions, Tasks, Events). The panel owns the data and the list items;
 * the shell owns the header, tabs, count, add row, filter dropdown, loading
 * and empty states, pager and the blocking spinner.
 *
 * Slots: default (list items), `filter` (dropdown body), `modals`.
 * Events: `tab` (key), `add`, `page` (number), `download`, `add-to-vault`.
 */
export default {
  name: "DashboardPanel",

  props: {
    title: { type: String, required: true },
    icon: { type: String, required: true },

    // [{ key, label }], highlighted by activeTab
    tabs: { type: Array, default: () => [] },
    activeTab: { type: [String, Number], default: null },

    // null hides the circle
    count: { type: [Number, String], default: null },

    addLabel: { type: String, default: "" },
    addPermissions: { type: Array, default: null },
    // route location; renders a router-link instead of a button
    addTo: { type: Object, default: null },

    filterable: { type: Boolean, default: false },
    // Download / Add to Vault row under the filter
    canExport: { type: Boolean, default: false },
    downloading: { type: Boolean, default: false },
    addingToVault: { type: Boolean, default: false },

    loading: { type: Boolean, default: false },
    empty: { type: Boolean, default: false },
    emptyText: { type: String, default: "Nothing found" },

    // Laravel paginator response
    pager: { type: Object, default: null },

    busy: { type: Boolean, default: false },
    busyText: { type: String, default: "Processing, please wait..." },
  },

  data() {
    return {
      showFilter: false,
    };
  },

  computed: {
    ...mapGetters({
      role: "getUserRole",
    }),

    addGuard() {
      return { roles: [this.role], permissions: this.addPermissions };
    },

    // Without a filter the row only holds the add action, so hide the whole
    // row from members who may not add. With a filter the row always shows.
    rowGuard() {
      return {
        roles: [this.role],
        permissions: this.filterable ? null : this.addPermissions,
      };
    },

    pages() {
      return hasPages(this.pager);
    },

    page() {
      return Number(this.pager && this.pager.current_page) || 1;
    },

    lastPage() {
      return Number(this.pager && this.pager.last_page) || 1;
    },

    pagerText() {
      return pagerLabel(this.pager);
    },
  },

  watch: {
    busy(isBusy) {
      if (isBusy) {
        this.$refs.busy.open();
      } else {
        this.$refs.busy.close();
      }
    },
  },
};
</script>
